import React from 'react'
import { Table } from '@tanstack/react-table'
import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react'
import { PaginationConfig } from '../table-types'
import { IconButton, Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@shopflo/ui'

/** Fixed-bottom bar: border-t (1) + py-3 (24) + 18px controls */
export const TABLE_PAGINATION_FOOTER_HEIGHT = 45

interface TablePaginationProps<T> {
  table: Table<T>
  pagination: PaginationConfig
  backgroundTransparent?: boolean
}

export function TablePagination<T>({ table, pagination, backgroundTransparent = false }: TablePaginationProps<T>) {
  const { pageIndex, pageSize, hasNextPage, pageSizeOptions, contentLeftPadding = 32, contentRightPadding, fixedBottom = false } = pagination
  const resolvedRightPadding = contentRightPadding ?? contentLeftPadding

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value, 10)
    if (newSize && pagination.onPaginationChange) {
      pagination.onPaginationChange({ pageIndex: 0, pageSize: newSize })
    }
  }

  // For manualPagination the server returns only the current page's rows.
  const currentPageRowCount = Math.min(table.getRowModel().rows.length, pageSize)
  const firstRow = currentPageRowCount === 0 ? 0 : pageIndex * pageSize + 1
  const lastRow = pageIndex * pageSize + currentPageRowCount

  const isPrevDisabled = pageIndex === 0
  const isNextDisabled = !hasNextPage && currentPageRowCount < pageSize

  return (
    <div
      className={fixedBottom
        ? `fixed bottom-0 z-10 flex items-center gap-2 border-t border-zinc-200/50 ${backgroundTransparent ? 'bg-transparent' : 'bg-zinc-50'} py-2 pl-3 pr-6`
        : `flex items-center gap-2 border-t border-zinc-200/50 ${backgroundTransparent ? 'bg-transparent' : 'bg-zinc-50'} py-3 pl-3 pr-6`
      }
      style={fixedBottom ? {
        left: `calc(var(--sidebar-width, 0px) + ${contentLeftPadding}px)`,
        right: `${resolvedRightPadding}px`,
        minHeight: TABLE_PAGINATION_FOOTER_HEIGHT,
        transition: 'right 0.3s ease-out'
      } : undefined}
    >
      <span className='text-text-xs-medium text-zinc-500'>Showing</span>

      {pageSizeOptions ? (
        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger
            className='h-fit w-fit border-none bg-transparent p-0 shadow-none focus:ring-0'
            aria-label='Rows per page'
            showCaret={false}
          >
            <div className='flex items-center gap-0.5'>
              <SelectValue
                placeholder={String(pageSize)}
                className='text-text-xs-medium text-zinc-800 !px-0 !py-0'
              />
              <CaretDown className='h-3 w-3 text-zinc-500' weight='bold' />
            </div>
          </SelectTrigger>
          <SelectContent align='start' side='top'>
            {pageSizeOptions.map(option => (
              <SelectItem key={option.value} value={String(option.value)} className='text-xs'>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      <span className='text-text-xs-medium text-zinc-500'>
        <span className='text-zinc-800'>{firstRow}</span>
        {' to '}
        <span className='text-zinc-800'>{lastRow}</span>
      </span>

      <div className='h-3 w-px flex-shrink-0 bg-zinc-200' />

      <div className='flex flex-row gap-1'>
        <IconButton
          IconComponent={CaretLeft}
          size='sm'
          variant='ghost'
          disabled={isPrevDisabled}
          onClick={() => pagination.onPaginationChange?.({ pageIndex: pageIndex - 1, pageSize })}
          aria-label='Previous page'
        />

        <IconButton
          IconComponent={CaretRight}
          size='sm'
          variant='ghost'
          disabled={isNextDisabled}
          onClick={() => pagination.onPaginationChange?.({ pageIndex: pageIndex + 1, pageSize })}
          aria-label='Next page'
        />
      </div>
    </div>
  )
}
